/**
 * Welcome access — a *temporary*, session-scoped unlock for Pro-locked
 * recipes in the categories the user picked during onboarding.
 *
 * Purpose:
 *   Give brand-new users an immediate taste of the value their onboarding
 *   choices unlock — but NEVER as permanent Pro access.
 *
 * Lifecycle:
 *   - Granted right after `POST /api/user/onboarding` succeeds with at least
 *     one interest selected.
 *   - Tied to the current `session_token` in localStorage (the one issued by
 *     the backend at login). When the user logs out, the auth context calls
 *     `clearWelcomeAccess()`. When they log back in, the new session token
 *     won't match the stored one so access is denied — exactly what the
 *     product spec calls for.
 *   - Has a hard 24-hour cap as a safety net in case the session lives long.
 *
 * Real Pro / admin users are NEVER affected by this — the lock check in
 * `RecipeCard.js` already short-circuits on `user.role !== 'guest'`.
 */

const STORAGE_KEY = 'welcome_access_v1';
const MAX_LIFETIME_MS = 24 * 60 * 60 * 1000; // 24 hours

// Same mapping the recipes-page partition uses. Kept here so onboarding and
// the card lock stay aligned without one importing from the other's page.
export const INTEREST_TO_TYPES = {
  slushice: ['klassisk', 'juice', 'sukkerfri', 'sport', 'sodavand'],
  frappe: ['kaffe'],
  milkshake: ['maelk', 'mælk', 'smoothie'],
  frozen_cocktails: ['cocktail'],
};

/** Returns the active session token (the one the welcome access is bound to). */
function currentSessionToken() {
  try {
    return localStorage.getItem('session_token') || '';
  } catch (_e) {
    return '';
  }
}

/**
 * Persist a welcome-access grant for the supplied interest keys.
 * Multiple interests are unioned into a flat set of recipe types.
 *
 * @param {string[]} interestKeys e.g. ['frappe','frozen_cocktails']
 */
export function grantWelcomeAccess(interestKeys) {
  if (!Array.isArray(interestKeys) || interestKeys.length === 0) {
    return;
  }
  const token = currentSessionToken();
  if (!token) return;

  const types = new Set();
  for (const key of interestKeys) {
    (INTEREST_TO_TYPES[key] || []).forEach((t) => types.add(t));
  }
  if (types.size === 0) return;

  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        session_token: token,
        types: Array.from(types),
        interests: [...interestKeys],
        granted_at: Date.now(),
        until: Date.now() + MAX_LIFETIME_MS,
      })
    );
  } catch (_e) {
    /* ignore — quota / private mode */
  }
}

/**
 * Read the currently active welcome access, if any.
 * Returns null when:
 *   - nothing stored
 *   - session token mismatches (user logged out/in)
 *   - past the 24h cap
 *   - payload is malformed
 */
export function getWelcomeAccess() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || typeof data !== 'object') return null;
    if (!Array.isArray(data.types) || data.types.length === 0) return null;
    if (typeof data.until !== 'number' || Date.now() >= data.until) return null;
    if (data.session_token !== currentSessionToken()) return null;
    return data;
  } catch (_e) {
    return null;
  }
}

/** Convenience predicate used by RecipeCard.isLocked. */
export function hasWelcomeAccessTo(recipeType) {
  const wa = getWelcomeAccess();
  if (!wa) return false;
  const t = String(recipeType || '').toLowerCase();
  return !!t && wa.types.includes(t);
}

export function clearWelcomeAccess() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (_e) {
    /* ignore */
  }
}
